import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, UploadCloud, Loader2, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { toast } from "sonner";

const opts = { withCredentials: true };

export default function ImportObraModal({ onClose, onImported }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [nombre, setNombre] = useState("");
  const [cliente, setCliente] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const doPreview = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) { toast.error("El archivo debe ser .xlsx"); return; }
    setFile(f); setLoading(true); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/obras/import-excel`, fd, opts);
      setPreview(data);
      setNombre(data.header?.obra || "");
      setCliente(data.header?.contratista || "");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "No se pudo leer el Excel"));
      if (d?.errors) setPreview({ ok: false, errors: d.errors });
    } finally { setLoading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) doPreview(f); };

  const confirm = async () => {
    if (!file) return;
    setConfirming(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/obras/import-excel/confirm?nombre=${encodeURIComponent(nombre)}&cliente_nombre=${encodeURIComponent(cliente)}`,
        fd, opts
      );
      toast.success(`Obra importada (${data.certificaciones_creadas} certificaciones)`);
      onImported && onImported();
      onClose();
      navigate(`/obra/${data._id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error al importar"));
    } finally { setConfirming(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="import-obra-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FileSpreadsheet size={18} className="text-emerald-600" /> Importar Obra desde Excel</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Dropzone */}
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-[#EA580C] bg-orange-50" : "border-gray-300 hover:border-[#EA580C]/50"}`}
            data-testid="import-dropzone"
          >
            <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={e => doPreview(e.target.files?.[0])} data-testid="import-file-input" />
            <UploadCloud size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-600 font-medium">{file ? file.name : "Arrastrá el Excel o hacé clic para seleccionar"}</p>
            <p className="text-xs text-gray-400 mt-1">Formato modelo CEN (.xlsx) — hoja "OTS Inst. Eléctrica"</p>
          </div>

          {loading && <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-4"><Loader2 size={16} className="animate-spin" /> Analizando archivo…</div>}

          {/* Errores */}
          {preview && !preview.ok && (preview.errors || []).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="import-errors">
              <p className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-2"><AlertTriangle size={16} /> El archivo tiene problemas</p>
              <ul className="list-disc pl-5 text-sm text-red-600 space-y-0.5">
                {preview.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {/* Vista previa OK */}
          {preview && preview.ok && (
            <div className="space-y-4" data-testid="import-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Rubros", preview.n_grupos], ["Ítems", preview.n_items], ["Certificaciones", preview.n_certificaciones], ["Total neto", null]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500">{l}</p>
                    <p className="text-lg font-bold text-gray-900">{v !== null ? v : formatPYG(preview.grand_total).replace("Gs. ", "")}</p>
                  </div>
                ))}
              </div>

              {preview.diff_warning && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="import-diff-warning">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {preview.diff_warning}
                </div>
              )}

              {(preview.warnings || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="import-warnings">
                  <p className="flex items-center gap-2 text-xs font-semibold text-amber-700 mb-1"><AlertTriangle size={14} /> Advertencias (no bloquean la importación)</p>
                  <ul className="list-disc pl-5 text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Nombre de la obra</label>
                  <input value={nombre} onChange={e => setNombre(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="import-nombre" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Cliente / Contratista</label>
                  <input value={cliente} onChange={e => setCliente(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="import-cliente" /></div>
              </div>

              <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50"><tr className="text-left">
                    <th className="px-2 py-1.5 font-semibold text-gray-500">N°</th>
                    <th className="px-2 py-1.5 font-semibold text-gray-500">Descripción</th>
                    <th className="px-2 py-1.5 font-semibold text-gray-500 text-right">Cant.</th>
                    <th className="px-2 py-1.5 font-semibold text-gray-500 text-right">Monto</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {(preview.items || []).slice(0, 60).map((it, i) => (
                      it.is_group
                        ? <tr key={i} className="bg-[#EBF3FB]"><td className="px-2 py-1 font-bold text-[#2E6DA4]">{it.numero}</td><td className="px-2 py-1 font-bold text-[#2E6DA4]" colSpan={3}>{it.descripcion}</td></tr>
                        : <tr key={i}><td className="px-2 py-1 text-gray-400 font-mono">{it.numero}</td><td className="px-2 py-1 text-gray-700">{it.descripcion}</td><td className="px-2 py-1 text-right tabular-nums text-gray-600">{Number(it.cantidad).toLocaleString("es-PY")}</td><td className="px-2 py-1 text-right tabular-nums text-gray-700">{formatPYG(it.monto)}</td></tr>
                    ))}
                  </tbody>
                </table>
                {(preview.items || []).length > 60 && <p className="text-center text-xs text-gray-400 py-2">… y {(preview.items.length - 60)} filas más</p>}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-5 py-2.5 text-sm font-medium">Cancelar</button>
          <button onClick={confirm} disabled={!preview?.ok || confirming} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl px-6 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="import-confirm-btn">
            {confirming ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Confirmar Importación
          </button>
        </div>
      </div>
    </div>, document.body);
}
